import React, { memo } from 'react';
import { Handle, Position } from 'reactflow';
import {
    Globe,
    Server,
    User,
    ShieldAlert,
    UserX,
    Monitor,
    Users,
    Folder,
    ShieldCheck,
    ChevronDown,
    ChevronRight,
} from 'lucide-react';

export interface ADNodeData {
    nodeType: 'domain' | 'dc' | 'ou' | 'user' | 'user_admin' | 'user_disabled' | 'computer' | 'group' | 'group_builtin' | 'container';
    label: string;
    subLabel?: string;
    dn?: string;
    isPrimary?: boolean;
    isSelected?: boolean;
    disabled?: boolean;
    admin?: boolean;
    hasChildren?: boolean;
    childCount?: number;
    isExpanded?: boolean;
    rawObject?: any;
    onToggleExpand?: (e: React.MouseEvent) => void;
}

const TYPE_CONFIG: Record<string, { icon: React.ElementType; color: string; borderAccent: string; badgeBg: string; textClass: string }> = {
    domain: {
        icon: Globe,
        color: 'text-blue-500 dark:text-blue-400',
        borderAccent: 'border-l-blue-500',
        badgeBg: 'bg-blue-500/10 text-blue-700 border-blue-500/30 dark:bg-blue-500/15 dark:text-blue-300',
        textClass: 'text-blue-500 dark:text-blue-400',
    },
    dc: {
        icon: Server,
        color: 'text-teal-600 dark:text-teal-400',
        borderAccent: 'border-l-teal-500',
        badgeBg: 'bg-teal-500/10 text-teal-700 border-teal-500/30 dark:bg-teal-500/15 dark:text-teal-300',
        textClass: 'text-teal-600 dark:text-teal-400',
    },
    ou: {
        icon: Folder,
        color: 'text-amber-500 dark:text-amber-400',
        borderAccent: 'border-l-amber-500',
        badgeBg: 'bg-amber-500/10 text-amber-700 border-amber-500/30 dark:bg-amber-500/15 dark:text-amber-300',
        textClass: 'text-amber-500 dark:text-amber-400',
    },
    user: {
        icon: User,
        color: 'text-emerald-600 dark:text-emerald-400',
        borderAccent: 'border-l-emerald-500',
        badgeBg: 'bg-emerald-500/10 text-emerald-700 border-emerald-500/30 dark:bg-emerald-500/15 dark:text-emerald-300',
        textClass: 'text-emerald-600 dark:text-emerald-400',
    },
    user_admin: {
        icon: ShieldAlert,
        color: 'text-red-500 dark:text-red-400',
        borderAccent: 'border-l-red-500',
        badgeBg: 'bg-red-500/10 text-red-700 border-red-500/30 dark:bg-red-500/15 dark:text-red-300',
        textClass: 'text-red-500 dark:text-red-400',
    },
    user_disabled: {
        icon: UserX,
        color: 'text-slate-400 dark:text-gray-400',
        borderAccent: 'border-l-slate-400 dark:border-l-gray-600',
        badgeBg: 'bg-slate-100 text-slate-500 border-slate-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700',
        textClass: 'text-slate-500 dark:text-gray-400',
    },
    computer: {
        icon: Monitor,
        color: 'text-purple-500 dark:text-purple-400',
        borderAccent: 'border-l-purple-500',
        badgeBg: 'bg-purple-500/10 text-purple-700 border-purple-500/30 dark:bg-purple-500/15 dark:text-purple-300',
        textClass: 'text-purple-500 dark:text-purple-400',
    },
    group: {
        icon: Users,
        color: 'text-indigo-500 dark:text-indigo-400',
        borderAccent: 'border-l-indigo-500',
        badgeBg: 'bg-indigo-500/10 text-indigo-700 border-indigo-500/30 dark:bg-indigo-500/15 dark:text-indigo-300',
        textClass: 'text-indigo-500 dark:text-indigo-400',
    },
    group_builtin: {
        icon: ShieldCheck,
        color: 'text-indigo-600 dark:text-indigo-300',
        borderAccent: 'border-l-indigo-400',
        badgeBg: 'bg-indigo-400/10 text-indigo-700 border-indigo-400/30 dark:bg-indigo-400/15 dark:text-indigo-200',
        textClass: 'text-indigo-600 dark:text-indigo-300',
    },
    container: {
        icon: Folder,
        color: 'text-slate-500 dark:text-slate-400',
        borderAccent: 'border-l-slate-400 dark:border-l-slate-600',
        badgeBg: 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700',
        textClass: 'text-slate-600 dark:text-slate-300',
    },
};

export const ADNode = memo(({ data }: { data: ADNodeData }) => {
    const config = TYPE_CONFIG[data.nodeType] || TYPE_CONFIG.user;
    const IconComponent = config.icon;
    const isSelected = data.isSelected;

    return (
        <div
            className={`relative min-w-[210px] max-w-[240px] h-[52px] rounded-lg px-3 py-2 bg-white dark:bg-gray-900/90 border border-slate-200 dark:border-gray-800 border-l-[4px] flex items-center justify-between gap-2.5 transition-all duration-200 shadow-md dark:shadow-lg backdrop-blur-md cursor-pointer ${
                isSelected
                    ? 'ring-2 ring-orange-500 border-orange-500/80 shadow-orange-500/25 scale-[1.03] z-30'
                    : 'hover:border-slate-300 dark:hover:border-gray-700 hover:scale-[1.01]'
            }`}
        >
            {/* Top & Bottom Handles */}
            <Handle
                type="target"
                position={Position.Top}
                className="!bg-slate-400 dark:!bg-gray-600 !w-2 !h-2 !border-2 !border-white dark:!border-gray-900 transition-colors"
            />
            <Handle
                type="source"
                position={Position.Bottom}
                className="!bg-orange-500 !w-2 !h-2 !border-2 !border-white dark:!border-gray-900 transition-colors"
            />

            {/* Left Icon Badge */}
            <div className={`p-1.5 rounded-md border shrink-0 flex items-center justify-center ${config.badgeBg}`}>
                <IconComponent size={14} className={config.color} />
            </div>

            {/* Middle Content */}
            <div className="flex flex-col min-w-0 flex-1 justify-center">
                <div className="flex items-center justify-between gap-1">
                    <span className="text-[11px] font-bold text-slate-900 dark:text-white truncate leading-tight tracking-wide" title={data.label}>
                        {data.label}
                    </span>
                </div>
                <span className="text-[9px] text-slate-500 dark:text-gray-400 font-mono truncate leading-tight mt-0.5" title={data.subLabel || data.dn}>
                    {data.subLabel || data.nodeType.toUpperCase()}
                </span>
            </div>

            {/* Right Status or Expand Badge */}
            {data.hasChildren && data.childCount && data.childCount > 0 ? (
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        if (data.onToggleExpand) data.onToggleExpand(e);
                    }}
                    className={`px-1.5 py-0.5 text-[9px] font-bold font-mono rounded flex items-center gap-0.5 transition-all shrink-0 ${
                        data.isExpanded
                            ? 'bg-orange-500/15 text-orange-600 dark:text-orange-400 border border-orange-500/40 hover:bg-orange-500/25'
                            : 'bg-slate-100 dark:bg-gray-800 text-slate-700 dark:text-gray-300 border border-slate-200 dark:border-gray-700 hover:bg-slate-200 dark:hover:bg-gray-700 hover:text-slate-900 dark:hover:text-white'
                    }`}
                    title={data.isExpanded ? 'Collapse children' : 'Expand children'}
                >
                    {data.isExpanded ? <ChevronDown size={10} /> : <ChevronRight size={10} />}
                    <span>{data.childCount}</span>
                </button>
            ) : data.admin ? (
                <span className="px-1.5 py-0.5 text-[8px] font-bold uppercase rounded bg-red-500/10 dark:bg-red-500/20 text-red-600 dark:text-red-400 border border-red-500/40 shrink-0">
                    ADM
                </span>
            ) : data.disabled ? (
                <span className="px-1.5 py-0.5 text-[8px] font-bold uppercase rounded bg-slate-100 dark:bg-gray-800 text-slate-500 dark:text-gray-400 border border-slate-200 dark:border-gray-700 shrink-0">
                    OFF
                </span>
            ) : (
                <div className="w-2 h-2 rounded-full bg-emerald-500 dark:bg-emerald-400 animate-pulse shrink-0" title="Active" />
            )}
        </div>
    );
});

ADNode.displayName = 'ADNode';
export default ADNode;
